import { ɵɵCdkFixedSizeVirtualScroll, ɵɵCdkVirtualForOf, ɵɵCdkVirtualScrollViewport } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, MatButtonToggle, MatButtonToggleChange, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/list';
import { MatFormField, MatLabel, MatOption, MatSelect, MatSelectChange } from '@angular/material/select';
import { PushPipe } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { FullscreenLayoutAreas } from '@src/assets/css/fullscreen.template.scss';
import { ModalService } from '@src/suite/common/components/modal/modal.service';
import { SearchType } from '@src/suite/common/enums/enums';
import { NavigationDirection } from '@src/suite/common/enums/navigation-direction';
import { LayoutChangeEvent, SidecarEfffectActions, SidecarMenu } from '@src/suite/common/store/fullscreen';
import {
    selectRundowns,
    selectSavedRundownViewStates,
    selectSidecarCurrentRundownSelectedStory,
    selectSidecarCurrentRundownVS,
    selectSidecarRundownSelectedLayout,
    selectSidecarSelectedRundown,
    selectSidecarSelectedRundownListSearchType,
} from '@src/suite/common/store/fullscreen/selectors/sidecar.selectors';
import { selectI18N, selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { formatLuxonISODateLocalTimeString } from '@src/suite/common/utils/date-utils';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';

import { SimpleSearchBoxAutocompleteComponent } from '../../components/search-box/simple-search-box-autocomplete/simple-search-box-autocomplete';
import { CardLeftBorderDirective } from '../../directives/card-left-border';
import { ClickStopPropagationDirective } from '../../directives/stop-propagation';
import { BLACK_LINE_GUID } from '../../enums/constants';
import { StRundownFullScreenDialogComponent } from '../st-rundown-full-screen/st-rundown-full-screen';
import { StoryFullScreenComponent } from '../stories-full-screen/story-full-screen/story-full-screen';

@Component({
    selector: 'st-rundown-list-sidecar',
    imports: [
        CardLeftBorderDirective,
        ClickStopPropagationDirective,
        MatButtonToggle,
        MatButtonToggleGroup,
        MatDivider,
        MatFormField,
        MatIcon,
        MatIconButton,
        MatLabel,
        MatOption,
        MatSelect,
        ɵɵCdkFixedSizeVirtualScroll,
        ɵɵCdkVirtualForOf,
        ɵɵCdkVirtualScrollViewport,
        PushPipe,
        SimpleSearchBoxAutocompleteComponent,
        StoryFullScreenComponent,
        StRundownFullScreenDialogComponent,
    ],
    templateUrl: './rundown-list-sidecar.html',
    styleUrl: './rundown-list-sidecar.scss',
    providers: [{ provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, useValue: { hideSingleSelectionIndicator: false } }],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-rundown-list-sidecar',
    },
})
export class RundownListSidecarComponent implements OnInit {
    SearchType = SearchType;
    NavigationDirection = NavigationDirection;

    store = inject(Store);
    modalService = inject(ModalService);
    readonly sidecarMenu = input<FullscreenLayoutAreas>();

    readonly selectedListSearchType = computed(() => {
        const selectedListSearchType = this.selectedListSearchTypeInternal();
        if (!selectedListSearchType) {
            return SearchType.SAVED;
        }
        return selectedListSearchType;
    });
    public readonly searchTerm = signal('');

    readonly selectedListSearchTypeInternal = computed(() => this.store.selectSignal(selectSidecarSelectedRundownListSearchType(this.sidecarMenu()))());
    readonly savedViewDropdownVisible = computed(() => this.selectedListSearchType() === SearchType.SAVED);
    // TODO: Use Store instead
    readonly rundowns = computed(() => {
        const rundowns = this.store.selectSignal(selectRundowns(this.sidecarMenu()))();
        return rundowns.map(rundown => ({ ...rundown, ModTimeDisplay: formatLuxonISODateLocalTimeString(rundown?.ModTime) }) as StorytellingRundownRecord & { ModTimeDisplay: string }) || [];
    });
    readonly selectSavedViewStates = computed(() => this.store.selectSignal(selectSavedRundownViewStates(this.sidecarMenu()))());
    readonly currentViewState = computed(() => this.store.selectSignal(selectSidecarCurrentRundownVS(this.sidecarMenu()))());
    readonly currentSelectedRundown = computed(() => this.store.selectSignal(selectSidecarSelectedRundown(this.sidecarMenu()))());
    readonly currentRundownSelectedStory = computed(() => this.store.selectSignal(selectSidecarCurrentRundownSelectedStory(this.sidecarMenu()))());
    readonly roLayout = computed(
        () => this.store.selectSignal(selectSidecarRundownSelectedLayout(this.sidecarMenu()))() ?? this.currentSelectedRundown()?.DefaultLayout ?? this.currentSelectedRundown()?.TemplateLayout
    );

    readonly currentChildList = computed(() => this.currentSelectedRundown()?.Sequence.filter(guid => guid !== BLACK_LINE_GUID) || []);

    readonly indexInParentToShow = computed(() => {
        const parent = this.currentSelectedRundown();
        const guids = this.currentChildList();

        if (parent && guids) {
            const indexInParent = guids?.findIndex(item => this.currentRundownSelectedStory().Guid === item);
            if (indexInParent == null) {
                return 0;
            }
            return indexInParent + 1;
        }
    });

    readonly totalCount = computed(() => this.currentChildList()?.length || 0);

    rundownsLabel$ = this.store.select(selectTranslatedKey(I18nKeys.PSTWebRundownsLabel));
    languageSettings = this.store.selectSignal(selectI18N)();

    ngOnInit() {
        if (!this.selectSavedViewStates()) {
            this.store.dispatch(SidecarEfffectActions.loadSidecarRundownViewStates({ sidecarType: this.sidecarMenu() }));
        }
    }

    onViewStateGuidChanged(viewState: MatSelectChange) {
        this.store.dispatch(SidecarEfffectActions.setSidecarRundownCurrentViewState({ guid: viewState.value, sidecarType: this.sidecarMenu() }));
    }

    openRundown(data: StorytellingRundownRecord) {
        this.store.dispatch(SidecarEfffectActions.openCurrentRundown({ recordGuid: data.Guid, sidecarMenu: this.sidecarMenu() }));
    }

    openStory(guid: string) {
        this.store.dispatch(SidecarEfffectActions.openStoryInSidecar({ guid, sidecarType: this.sidecarMenu(), sidecarMenu: SidecarMenu.RUNDOWNS }));
    }
    changeRoLayout(event: LayoutChangeEvent, roGuid: string) {
        this.store.dispatch(SidecarEfffectActions.setRundownLayout({ roGuid, layout: event, sidecar: this.sidecarMenu() }));
    }

    public async toggleSearchTab(event: MatButtonToggleChange) {
        this.store.dispatch(SidecarEfffectActions.setRundownsListSearchType({ searchType: event.value, sidecarType: this.sidecarMenu() }));
        if (this.selectedListSearchType() === SearchType.SEARCH) {
            this.store.dispatch(SidecarEfffectActions.clearSearchRundowns({ sidecarType: this.sidecarMenu() }));
        }
        if (this.savedViewDropdownVisible()) {
            this.searchTerm.set('');
            this.store.dispatch(SidecarEfffectActions.reloadSidecarRundownsBasedOnCurrentViewState({ sidecarType: this.sidecarMenu() }));
        }
    }

    public onSearchHandler(value: string | number) {
        this.store.dispatch(SidecarEfffectActions.searchRundowns({ searchTerm: value.toString(), sidecarType: this.sidecarMenu() }));
    }

    public navigate(direction: NavigationDirection) {
        this.store.dispatch(SidecarEfffectActions.navigateStoryInCurrentRO({ direction, sidecarMenu: this.sidecarMenu() }));
    }
}
